<template>
  <!-- Add Trainer -->
  <div class="modal fade dq-trainers-modal" id="new_trainer" tabindex="-1">
    <div
      class="modal-dialog modal-dialog-centered modal-md modal-dialog-scrollable dq-dialog"
    >
      <div class="modal-content dq-modal-content" :key="addKey">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-indigo">
              <i class="ti ti-user-plus"></i>
            </div>
            <div class="dq-headtext">
              <h4 class="modal-title dq-title mb-0">Add Trainer</h4>
              <div class="dq-subtitle">
                Create a trainer profile with contact details, expertise and a
                photo.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="submitCreate">
          <div class="modal-body dq-modal-body">
            <!-- Photo uploader (premium) -->
            <div class="dq-card dq-uploader">
              <div class="dq-uploader-left">
                <div class="dq-avatar">
                  <img
                    v-if="form.imageUrl"
                    :src="form.imageUrl"
                    alt="Trainer photo preview"
                  />
                  <div v-else class="dq-avatar-empty">
                    <i class="ti ti-photo"></i>
                  </div>
                </div>

                <div class="dq-uploader-meta">
                  <div class="dq-uploader-title">Profile Photo</div>
                  <div class="dq-uploader-sub">
                    PNG/JPG recommended. Max 4MB.
                  </div>

                  <div class="dq-uploader-actions">
                    <label
                      class="btn btn-primary dq-btn dq-btn-primary dq-upload-btn"
                    >
                      <i class="ti ti-upload me-2"></i> Upload
                      <input
                        type="file"
                        accept="image/*"
                        class="dq-file"
                        @change="onImageChange"
                      />
                    </label>

                    <button
                      v-if="form.imageUrl"
                      type="button"
                      class="btn btn-light dq-btn dq-btn-muted"
                      @click="removeImage"
                    >
                      <i class="ti ti-x me-1"></i> Remove
                    </button>
                  </div>
                </div>
              </div>

              <div class="dq-uploader-right">
                <div class="dq-hint">
                  <i class="ti ti-bulb"></i>
                  <span>
                    Tip: Add a clear headshot for better training directory
                    visibility.
                  </span>
                </div>
              </div>
            </div>

            <!-- Details -->
            <div class="dq-card mt-3">
              <div class="dq-card-head">
                <div class="dq-card-title">
                  <i class="ti ti-id"></i> Trainer Details
                </div>
                <div class="dq-card-sub">
                  Fill in the trainer’s identity, contacts, and specialization.
                </div>
              </div>

              <div class="row g-3 mt-0">
                <div class="col-md-6">
                  <label class="form-label dq-label">
                    First Name <span class="text-danger">*</span>
                  </label>
                  <input
                    v-model.trim="form.first_name"
                    class="form-control dq-input"
                    placeholder="e.g. John"
                    autocomplete="off"
                  />
                </div>

                <div class="col-md-6">
                  <label class="form-label dq-label">
                    Last Name <span class="text-danger">*</span>
                  </label>
                  <input
                    v-model.trim="form.last_name"
                    class="form-control dq-input"
                    placeholder="e.g. Doe"
                    autocomplete="off"
                  />
                </div>

                <div class="col-md-6">
                  <label class="form-label dq-label">
                    Phone <span class="text-danger">*</span>
                  </label>
                  <input
                    v-model.trim="form.phone"
                    class="form-control dq-input"
                    placeholder="e.g. 0712 345 678"
                    autocomplete="off"
                  />
                </div>

                <div class="col-md-6">
                  <label class="form-label dq-label">
                    Email <span class="text-danger">*</span>
                  </label>
                  <input
                    v-model.trim="form.email"
                    class="form-control dq-input"
                    placeholder="e.g. john.doe@example.com"
                    autocomplete="off"
                  />
                </div>

                <div class="col-12">
                  <label class="form-label dq-label">
                    Expertise <span class="text-danger">*</span>
                  </label>
                  <input
                    v-model.trim="form.expertise"
                    class="form-control dq-input"
                    placeholder="e.g. Leadership, Compliance, Agile, HR Policy…"
                    autocomplete="off"
                  />
                  <div class="dq-help">
                    Keep it short and searchable (1–3 keywords works best).
                  </div>
                </div>

                <div class="col-12">
                  <label class="form-label dq-label">
                    Bio / Description <span class="text-danger">*</span>
                  </label>
                  <textarea
                    v-model.trim="form.bio"
                    class="form-control dq-input"
                    rows="4"
                    placeholder="Write a short professional bio…"
                  ></textarea>
                </div>

                <div class="col-12">
                  <label class="form-label dq-label">Status</label>
                  <vue-select
                    v-model="form.status"
                    :options="['Active', 'Inactive']"
                    placeholder="Select Status"
                    class="dq-vselect"
                    :clearable="false"
                  />

                  <div
                    class="dq-chip mt-2"
                    :class="statusChipClass(form.status)"
                  >
                    <i class="ti ti-circle-check"></i>
                    <span>{{ statusChipText(form.status) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer dq-modal-footer">
            <button
              type="button"
              class="btn btn-light dq-btn dq-btn-muted me-2"
              data-bs-dismiss="modal"
              :class="{ disabled: addingTrainer }"
            >
              Cancel
            </button>

            <button type="submit" class="btn btn-primary dq-btn dq-btn-primary">
              <span
                v-if="addingTrainer"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              Add Trainer <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Edit Trainer -->
  <div class="modal fade dq-trainers-modal" id="edit_trainer" tabindex="-1">
    <div
      class="modal-dialog modal-dialog-centered modal-md modal-dialog-scrollable dq-dialog"
    >
      <div class="modal-content dq-modal-content" :key="editKey">
        <!-- Header -->
        <div class="modal-header dq-modal-header dq-amber-head">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-amber">
              <i class="ti ti-edit"></i>
            </div>
            <div class="dq-headtext">
              <h4 class="modal-title dq-title mb-0">Edit Trainer</h4>
              <div class="dq-subtitle">
                Update profile details, photo and availability status.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="submitEdit">
          <div class="modal-body dq-modal-body">
            <!-- Photo uploader -->
            <div class="dq-card dq-uploader">
              <div class="dq-uploader-left">
                <div class="dq-avatar">
                  <img
                    v-if="form.imageUrl"
                    :src="form.imageUrl"
                    alt="Trainer photo preview"
                  />
                  <div v-else class="dq-avatar-empty">
                    <i class="ti ti-photo"></i>
                  </div>
                </div>

                <div class="dq-uploader-meta">
                  <div class="dq-uploader-title">Profile Photo</div>
                  <div class="dq-uploader-sub">
                    Upload only if you want to replace the existing photo.
                  </div>

                  <div class="dq-uploader-actions">
                    <label
                      class="btn btn-primary dq-btn dq-btn-primary dq-upload-btn"
                    >
                      <i class="ti ti-upload me-2"></i> Upload
                      <input
                        type="file"
                        accept="image/*"
                        class="dq-file"
                        @change="onImageChange"
                      />
                    </label>

                    <button
                      v-if="form.imageUrl"
                      type="button"
                      class="btn btn-light dq-btn dq-btn-muted"
                      @click="removeImage"
                    >
                      <i class="ti ti-x me-1"></i> Remove
                    </button>
                  </div>
                </div>
              </div>

              <div class="dq-uploader-right">
                <div class="dq-hint">
                  <i class="ti ti-shield-check"></i>
                  <span>
                    This photo is visible wherever trainers are listed.
                  </span>
                </div>
              </div>
            </div>

            <!-- Details -->
            <div class="dq-card mt-3">
              <div class="dq-card-head">
                <div class="dq-card-title">
                  <i class="ti ti-settings"></i> Update Details
                </div>
                <div class="dq-card-sub">Make changes then save.</div>
              </div>

              <div class="row g-3 mt-0">
                <div class="col-md-6">
                  <label class="form-label dq-label">
                    First Name <span class="text-danger">*</span>
                  </label>
                  <input
                    v-model.trim="form.first_name"
                    class="form-control dq-input"
                    placeholder="e.g. John"
                  />
                </div>

                <div class="col-md-6">
                  <label class="form-label dq-label">
                    Last Name <span class="text-danger">*</span>
                  </label>
                  <input
                    v-model.trim="form.last_name"
                    class="form-control dq-input"
                    placeholder="e.g. Doe"
                  />
                </div>

                <div class="col-md-6">
                  <label class="form-label dq-label">
                    Phone <span class="text-danger">*</span>
                  </label>
                  <input
                    v-model.trim="form.phone"
                    class="form-control dq-input"
                    placeholder="e.g. 0712 345 678"
                  />
                </div>

                <div class="col-md-6">
                  <label class="form-label dq-label">
                    Email <span class="text-danger">*</span>
                  </label>
                  <input
                    v-model.trim="form.email"
                    class="form-control dq-input"
                    placeholder="e.g. john.doe@example.com"
                  />
                </div>

                <div class="col-12">
                  <label class="form-label dq-label">
                    Expertise <span class="text-danger">*</span>
                  </label>
                  <input
                    v-model.trim="form.expertise"
                    class="form-control dq-input"
                    placeholder="e.g. Leadership, Compliance, Agile…"
                  />
                </div>

                <div class="col-12">
                  <label class="form-label dq-label">
                    Bio / Description <span class="text-danger">*</span>
                  </label>
                  <textarea
                    v-model.trim="form.bio"
                    class="form-control dq-input"
                    rows="4"
                    placeholder="Write a short professional bio…"
                  ></textarea>
                </div>

                <div class="col-12">
                  <label class="form-label dq-label">Status</label>

                  <vue-select
                    v-model="form.status"
                    :options="['Active', 'Inactive']"
                    placeholder="Select Status"
                    class="dq-vselect"
                    :clearable="false"
                  />

                  <div
                    class="dq-chip mt-2"
                    :class="statusChipClass(form.status)"
                  >
                    <i class="ti ti-circle-check"></i>
                    <span>{{ statusChipText(form.status) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer dq-modal-footer">
            <button
              type="button"
              class="btn btn-light dq-btn dq-btn-muted me-2"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>

            <button
              type="submit"
              class="btn btn-primary dq-btn dq-btn-primary"
              :disabled="!isEdit"
            >
              <span
                v-if="addingTrainer"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              Save Changes <i class="ti ti-device-floppy ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Delete Modal -->
  <div class="modal fade dq-trainers-modal" id="delete_modal" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-sm">
      <div class="modal-content dq-modal-content">
        <div class="modal-header dq-modal-header dq-danger-head">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-red">
              <i class="ti ti-trash"></i>
            </div>
            <div class="dq-headtext">
              <h5 class="modal-title dq-title mb-0">Confirm Delete</h5>
              <div class="dq-subtitle">This action can’t be undone.</div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <div class="modal-body dq-modal-body">
          <div class="dq-danger">
            <i class="ti ti-alert-octagon"></i>
            <div>
              <div class="dq-danger-title">Delete this trainer?</div>
              <div class="dq-danger-sub">
                Are you sure you want to delete this trainer record?
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer dq-modal-footer">
          <a
            href="javascript:void(0);"
            class="btn btn-light dq-btn dq-btn-muted me-2"
            data-bs-dismiss="modal"
          >
            Cancel
          </a>

          <button
            class="btn btn-danger dq-btn dq-btn-danger"
            :disabled="deleting"
            @click="confirmDelete"
          >
            <span
              v-if="deleting"
              class="spinner-border spinner-border-sm me-2"
            ></span>
            Delete <i class="ti ti-trash ms-2"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from "vue";
import { useTrainerStore } from "@/stores/trainerStore";
import { toast } from "@/utils/toast";

const props = defineProps({
  editItem: { type: Object, default: null },
});
const emit = defineEmits(["saved"]);

const trainerStore = useTrainerStore();

const deleting = ref(false);
const addingTrainer = computed(() => trainerStore.loading);

const addKey = ref(0);
const editKey = ref(0);

const form = ref(blankForm());

function blankForm() {
  return {
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    expertise: "",
    imageUrl: "",
    photo_url: null,
    bio: "",
    status: "Active",
  };
}

function resetForm() {
  const blank = blankForm();
  for (const k in blank) form.value[k] = blank[k];
}

/* Image preview & removal */
function onImageChange(e) {
  const file = e.target.files?.[0];
  if (!file) return;

  // lightweight client-side limit (matches your UI statement)
  const max = 4 * 1024 * 1024;
  if (file.size > max) {
    toast("Image must be below 4MB", "error");
    e.target.value = "";
    return;
  }

  form.value.photo_url = file;

  const reader = new FileReader();
  reader.onload = (evt) => {
    form.value.imageUrl = evt.target?.result || "";
  };
  reader.readAsDataURL(file);
}
function removeImage() {
  form.value.photo_url = null;
  form.value.imageUrl = "";
}

/* Sync edit item into form */
watch(
  () => props.editItem,
  (val) => {
    if (val) {
      form.value.first_name = val.first_name ?? "";
      form.value.last_name = val.last_name ?? "";
      form.value.phone = val.phone ?? "";
      form.value.email = val.email ?? "";
      form.value.expertise = val.expertise ?? "";
      form.value.bio = val.bio ?? "";
      form.value.status = val.status ?? "Active";

      // preview existing backend image (if any)
      form.value.imageUrl =
        val.photo_url || val.image_url || val.avatar_url || "";
      form.value.photo_url = null;

      editKey.value++;
    } else {
      resetForm();
    }
  },
  { immediate: true }
);

const isEdit = computed(() => !!props.editItem?.id);

async function submitCreate() {
  if (
    !form.value.first_name ||
    !form.value.last_name ||
    !form.value.phone ||
    !form.value.email ||
    !form.value.expertise ||
    !form.value.bio
  ) {
    toast("All fields are required", "error");
    return;
  }

  const fd = new FormData();
  for (const [k, v] of Object.entries(form.value)) {
    if (k === "photo_url") {
      if (v instanceof File) fd.append("photo_url", v);
    } else if (k !== "imageUrl") {
      fd.append(k, v ?? "");
    }
  }

  await trainerStore.create(fd);
  closeNew();
  resetForm();
  addKey.value++;
  emit("saved");
}

async function submitEdit() {
  if (!props.editItem?.id) return;

  const fd = new FormData();
  fd.append("_method", "PUT"); // ✅ common Laravel multipart update pattern

  for (const [k, v] of Object.entries(form.value)) {
    if (k === "photo_url") {
      if (v instanceof File) fd.append("photo_url", v); // only send if changed
    } else if (k !== "imageUrl") {
      fd.append(k, v ?? "");
    }
  }

  await trainerStore.update(props.editItem.id, fd);
  closeEdit();
  resetForm();
  editKey.value++;
  emit("saved");
}

function closeNew() {
  document.querySelector('#new_trainer [data-bs-dismiss="modal"]')?.click();
}
function closeEdit() {
  document.querySelector('#edit_trainer [data-bs-dismiss="modal"]')?.click();
}

async function confirmDelete() {
  deleting.value = true;
  try {
    if (!props.editItem?.id) return;
    await trainerStore.remove(props.editItem.id);
    emit("saved");
  } catch (e) {
    console.error(e);
  } finally {
    deleting.value = false;
  }
}

/* status chips */
function statusChipClass(v) {
  const x = String(v || "").toLowerCase();
  if (x === "active") return "dq-chip-ok";
  if (x === "inactive") return "dq-chip-muted";
  return "dq-chip-muted";
}
function statusChipText(v) {
  const x = String(v || "");
  return x ? `Status: ${x}` : "Status";
}

/* lifecycle reset */
let cleanup = null;
onMounted(() => {
  const addEl = document.getElementById("new_trainer");
  const editEl = document.getElementById("edit_trainer");

  const handleAddShow = () => {
    resetForm();
    addKey.value++;
  };
  const handleAddHidden = () => resetForm();
  const handleEditHidden = () => {
    resetForm();
    editKey.value++;
  };

  addEl?.addEventListener("show.bs.modal", handleAddShow);
  addEl?.addEventListener("hidden.bs.modal", handleAddHidden);
  editEl?.addEventListener("hidden.bs.modal", handleEditHidden);

  cleanup = () => {
    addEl?.removeEventListener("show.bs.modal", handleAddShow);
    addEl?.removeEventListener("hidden.bs.modal", handleAddHidden);
    editEl?.removeEventListener("hidden.bs.modal", handleEditHidden);
  };
});

onBeforeUnmount(() => cleanup && cleanup());
</script>

<style scoped>
.dq-dialog {
  max-width: 760px;
}

/* Modal shell */
.dq-modal-content {
  border-radius: 22px;
  border: none;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.08),
    0 40px 90px rgba(15, 23, 42, 0.14);
}
.dq-modal-header {
  padding: 18px 20px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: linear-gradient(180deg, rgba(99, 102, 241, 0.1), #fff);
}
.dq-amber-head {
  background: linear-gradient(180deg, rgba(245, 158, 11, 0.12), #fff);
}
.dq-danger-head {
  background: linear-gradient(180deg, rgba(239, 68, 68, 0.12), #fff);
}
.dq-modal-body {
  padding: 16px 20px;
}

/* Scroll + sticky footer */
.modal-dialog.modal-dialog-scrollable .modal-content {
  max-height: 92vh;
  display: flex;
  flex-direction: column;
}
.modal-dialog.modal-dialog-scrollable .modal-body {
  overflow-y: auto;
  max-height: calc(92vh - 150px);
  overscroll-behavior: contain;
}
.dq-modal-footer {
  padding: 14px 20px;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
  position: sticky;
  bottom: 0;
  z-index: 2;
}

/* Header bits */
.dq-titlewrap {
  display: flex;
  align-items: center;
  gap: 12px;
}
.dq-title {
  font-weight: 950;
  letter-spacing: -0.35px;
}
.dq-subtitle {
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.6);
}
.dq-close {
  opacity: 0.9;
}
.dq-close:hover {
  opacity: 1;
}

/* Hero icon */
.dq-hero {
  width: 46px;
  height: 46px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}
.dq-hero-indigo {
  background: rgba(99, 102, 241, 0.14);
  color: rgb(79, 70, 229);
}
.dq-hero-amber {
  background: rgba(245, 158, 11, 0.14);
  color: rgb(217, 119, 6);
}
.dq-hero-red {
  background: rgba(239, 68, 68, 0.14);
  color: rgb(220, 38, 38);
}

/* Cards */
.dq-card {
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
  padding: 14px;
}
.dq-card-head {
  padding: 2px 2px 10px;
}
.dq-card-title {
  font-weight: 950;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.dq-card-sub {
  margin-top: 6px;
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.6);
}

/* Uploader */
.dq-uploader {
  display: flex;
  gap: 14px;
  align-items: stretch;
  justify-content: space-between;
}
.dq-uploader-left {
  display: flex;
  gap: 14px;
  align-items: center;
  flex: 1 1 auto;
}
.dq-uploader-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 0 0 auto;
}
.dq-avatar {
  width: 76px;
  height: 76px;
  border-radius: 22px;
  border: 1px dashed rgba(15, 23, 42, 0.18);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.02);
}
.dq-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.dq-avatar-empty {
  color: rgba(15, 23, 42, 0.45);
  font-size: 22px;
}
.dq-uploader-meta {
  flex: 1;
  min-width: 0;
}
.dq-uploader-title {
  font-weight: 950;
}
.dq-uploader-sub {
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.6);
  margin-top: 2px;
}
.dq-uploader-actions {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-top: 10px;
  flex-wrap: wrap;
}
.dq-upload-btn {
  position: relative;
  overflow: hidden;
}
.dq-file {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}

/* Hint */
.dq-hint {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 16px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: rgba(15, 23, 42, 0.02);
  color: rgba(15, 23, 42, 0.72);
  font-weight: 800;
  font-size: 0.82rem;
  max-width: 260px;
}
.dq-hint i {
  color: rgba(99, 102, 241, 0.85);
}

/* Inputs */
.dq-label {
  font-size: 0.82rem;
  font-weight: 900;
}
.dq-input {
  border-radius: 14px;
  padding: 10px 12px;
}
.dq-input:focus {
  border-color: rgba(99, 102, 241, 0.35);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
}
.dq-help {
  margin-top: 6px;
  font-size: 0.8rem;
  color: rgba(15, 23, 42, 0.55);
}

/* Buttons */
.dq-btn {
  border-radius: 12px;
  padding: 10px 16px;
  font-weight: 900;
}
.dq-btn-muted {
  background: rgba(15, 23, 42, 0.04);
  border: 1px solid rgba(15, 23, 42, 0.06);
}
.dq-btn-primary {
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.1);
}
.dq-btn-danger {
  box-shadow: 0 12px 24px rgba(239, 68, 68, 0.12);
}

/* Chips */
.dq-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 14px;
  font-size: 0.82rem;
  font-weight: 900;
  border: 1px solid rgba(15, 23, 42, 0.06);
}
.dq-chip-muted {
  background: rgba(15, 23, 42, 0.04);
  color: rgba(15, 23, 42, 0.7);
}
.dq-chip-ok {
  background: rgba(16, 185, 129, 0.12);
  color: rgb(5, 150, 105);
}

/* Delete warning */
.dq-danger {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px;
  border-radius: 18px;
  border: 1px solid rgba(239, 68, 68, 0.14);
  background: rgba(239, 68, 68, 0.08);
}
.dq-danger i {
  font-size: 18px;
  color: rgb(220, 38, 38);
  margin-top: 2px;
}
.dq-danger-title {
  font-weight: 950;
}
.dq-danger-sub {
  margin-top: 6px;
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.7);
}

@media (max-width: 576px) {
  .dq-dialog {
    max-width: 96vw;
    margin: 0.75rem auto;
  }
  .dq-uploader {
    flex-direction: column;
  }
  .dq-uploader-right {
    justify-content: flex-start;
  }
  .dq-hint {
    max-width: 100%;
  }
}
</style>
